// =============================================================================
// VandieZ shipper-web -- lightweight toast notifications
// =============================================================================
// Mirrors admin-web's toast module. Self-contained replacement for
// sonner / react-hot-toast.
//
//   <ToastProvider>            // mounted once in AppShell
//     <App/>
//   </ToastProvider>
//
//   const toast = useToast();
//   toast.success("Profile saved.");
//   toast.error("Could not save profile.");
//
// Toasts auto-dismiss after 3.5 s; up to 4 stay on screen at once.
// Bottom-right placement; respects dark mode via existing CSS variables.
// =============================================================================

import { CheckCircle2, X, XCircle } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type React from "react";

import { cn } from "@/lib/utils";

type ToastKind = "success" | "error";

interface ToastEntry {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const AUTO_DISMISS_MS = 3500;
const MAX_VISIBLE = 4;

export function ToastProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  const idRef = useRef<number>(0);

  const dismiss = useCallback((id: number) => {
    setEntries((cur) => cur.filter((e) => e.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      idRef.current += 1;
      const id = idRef.current;
      setEntries((cur) => {
        const next = [...cur, { id, kind, message }];
        return next.slice(-MAX_VISIBLE);
      });
      window.setTimeout(() => { dismiss(id); }, AUTO_DISMISS_MS);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (m: string) => { push("success", m); },
      error: (m: string) => { push("error", m); },
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport entries={entries} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

// Silent no-op when called outside <ToastProvider>. The shell mounts
// the provider so this only fires in unit tests that render a bare page.
const NOOP_TOAST: ToastApi = {
  success: () => undefined,
  error: () => undefined,
  dismiss: () => undefined,
};

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  return ctx ?? NOOP_TOAST;
}

function ToastViewport({
  entries,
  onDismiss,
}: {
  entries: ToastEntry[];
  onDismiss: (id: number) => void;
}): React.JSX.Element {
  return (
    <div
      // Top-right placement — bottom-right was colliding with primary
      // form action buttons (e.g. wallet Top-up modal's Generate-QR
      // button). Top-right is the conventional placement for transient
      // notifications and stays out of the way of action buttons.
      //
      // Mobile (<md): drop below the sticky mobile top-bar (which is
      // ~44px tall) so the toast doesn't sit on top of the hamburger.
      // md+: original top-4 since there's no mobile bar.
      className="fixed top-16 right-4 z-[60] flex flex-col gap-2 pointer-events-none max-w-[calc(100vw-2rem)] sm:max-w-sm md:top-4"
      aria-live="polite"
      aria-atomic="true"
    >
      {entries.map((e) => (
        <ToastRow key={e.id} entry={e} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastRow({
  entry,
  onDismiss,
}: {
  entry: ToastEntry;
  onDismiss: (id: number) => void;
}): React.JSX.Element {
  const [shown, setShown] = useState<boolean>(false);
  useEffect(() => {
    const handle = window.setTimeout(() => { setShown(true); }, 10);
    return () => { window.clearTimeout(handle); };
  }, []);

  const Icon = entry.kind === "success" ? CheckCircle2 : XCircle;
  return (
    <div
      className={cn(
        "pointer-events-auto flex items-center gap-2 rounded-md border bg-popover px-3 py-2 shadow-lg",
        "text-sm transition-all duration-200",
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
        entry.kind === "success"
          ? "border-success"
          : "border-danger",
      )}
      role={entry.kind === "error" ? "alert" : "status"}
    >
      <Icon
        className={cn(
          "h-4 w-4 shrink-0",
          entry.kind === "success" ? "text-success" : "text-danger",
        )}
        aria-hidden
      />
      <span className="flex-1 break-words max-w-xs">{entry.message}</span>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => { onDismiss(entry.id); }}
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
